import React, { useEffect, useRef } from 'react';
import SplitText from './SplitText';
import { gsap, prefersReducedMotion } from './gsap';
import './landing.css';

type PageHeroProps = {
  eyebrow?: string;
  title: string;
  subtitle?: string;
  children?: React.ReactNode;
};

/** En-tête de page secondaire (titre lettre par lettre + sous-titre). */
const PageHero: React.FC<PageHeroProps> = ({ eyebrow, title, subtitle, children }) => {
  const ref = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (prefersReducedMotion()) return;
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: 'power3.out' } });
      tl.fromTo('.ts-page-hero-eyebrow', { autoAlpha: 0, y: 14 }, { autoAlpha: 1, y: 0, duration: 0.5 }, 0.1)
        .fromTo(
          '.ts-char',
          { yPercent: 110, autoAlpha: 0, rotateX: -40 },
          { yPercent: 0, autoAlpha: 1, rotateX: 0, duration: 0.8, stagger: 0.02, ease: 'power4.out' },
          0.2,
        )
        .fromTo('.ts-page-hero-sub', { autoAlpha: 0, y: 20 }, { autoAlpha: 1, y: 0, duration: 0.6 }, '-=0.5')
        .fromTo('.ts-page-hero-extra', { autoAlpha: 0, y: 16 }, { autoAlpha: 1, y: 0, duration: 0.5 }, '-=0.35');
    }, el);
    return () => ctx.revert();
  }, []);

  return (
    <section className="ts-page-hero" ref={ref}>
      <div className="ts-blob ts-blob--peach ts-page-hero-blob-1" data-speed="0.85" />
      <div className="ts-blob ts-blob--lilac ts-page-hero-blob-2" data-speed="1.1" />
      <div className="ts-page-hero-inner">
        {eyebrow && (
          <span className="ts-eyebrow ts-page-hero-eyebrow">
            <span className="ts-eyebrow-dot" />
            {eyebrow}
          </span>
        )}
        <h1 className="ts-title ts-page-hero-title">
          <span className="ts-page-hero-line">
            <SplitText text={title} />
          </span>
        </h1>
        {subtitle && <p className="ts-lead ts-page-hero-sub">{subtitle}</p>}
        {children && <div className="ts-page-hero-extra">{children}</div>}
      </div>
    </section>
  );
};

export default PageHero;
